/**
 * Support API HTTP Client
 * Routes requests to the support-api backend (separate microservice)
 */

import { parseResponse } from "./response-parser";
import { FetchError } from "./errors";
import { DEFAULT_TIMEOUT, HTTP_METHODS } from "./constants";
import type { FetchOptions } from "./types";

const SUPPORT_BASE_URL =
  process.env.NEXT_PUBLIC_SUPPORT_API_URL ||
  process.env.SUPPORT_API_URL ||
  "http://localhost:8006";

async function supportFetch<T>(
  path: string,
  options?: FetchOptions,
): Promise<T> {
  const { params, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options || {};

  const cleanPath = path.replace(/^\//, "");
  const fullUrl = new URL(`${SUPPORT_BASE_URL}/api/v1/${cleanPath}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fullUrl.searchParams.set(key, String(value));
      }
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(fullUrl.toString(), {
      ...fetchOptions,
      signal: controller.signal,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      let errorMessage = `HTTP error: ${response.statusText}`;
      try {
        const errorBody = await response.json();
        if (errorBody.detail) {
          errorMessage = errorBody.detail;
        }
      } catch {
        // Response body is not JSON
      }
      throw new FetchError(
        errorMessage,
        response.status,
        response.statusText,
        fullUrl.toString(),
        fetchOptions.method,
      );
    }

    return parseResponse<T>(response);
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new FetchError(
        "Request timeout",
        408,
        "Request Timeout",
        fullUrl.toString(),
        fetchOptions.method,
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const supportGet = <T>(
  url: string,
  options?: FetchOptions,
): Promise<T> => supportFetch<T>(url, { ...options, method: HTTP_METHODS.GET });

export const supportPost = <TResponse>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<TResponse> =>
  supportFetch<TResponse>(url, {
    ...options,
    method: HTTP_METHODS.POST,
    body: JSON.stringify(body),
  });

export const supportPut = <TResponse>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<TResponse> =>
  supportFetch<TResponse>(url, {
    ...options,
    method: HTTP_METHODS.PUT,
    body: JSON.stringify(body),
  });

export const supportPatch = <TResponse>(
  url: string,
  body: unknown,
  options?: FetchOptions,
): Promise<TResponse> =>
  supportFetch<TResponse>(url, {
    ...options,
    method: HTTP_METHODS.PATCH,
    body: JSON.stringify(body),
  });
